<template>
  <div :class="$style.orderListWindow">
    <!-- 상단 앱바 -->
    <div :class="$style.chatHeader">
      <button :class="$style.backButton" @click="$emit('close')" aria-label="뒤로가기">
        <img :src="backIconUrl" alt="뒤로가기" :class="$style.backIcon" />
      </button>
      <div :class="$style.titleGroup">
        <h5>주문목록</h5>
        <span :class="$style.subtitle">주문하신 상품을 선택해주세요</span>
      </div>
    </div>

    <!-- 주문 목록 컨텐츠 -->
    <div :class="$style.orderContent" ref="orderContentRef">
      <div :class="$style.ordersContainer">
        <div
          v-for="(order, index) in limitedOrders"
          :key="index"
          :class="[$style.orderItemWrapper]"
          @click="selectOrder(order)"
        >
          <div :class="[$style.orderItem, { [$style.selected]: selectedOrder === order }]">
            <div :class="$style.orderDetails">
              <div :class="$style.orderHeader">
                <div :class="$style.orderInfo">
                  <div :class="$style.orderTitle">
                    {{ order.title || (order.raw?.ordtitle ? order.raw.ordtitle : `주문 #${order.orderNo}`) }}
                  </div>
                  <div :class="$style.orderDate">주문일자: {{ formatDate(order.orderDate || order.raw?.regdt) }}</div>
                  <div v-if="order.raw?.dboxnum" :class="$style.orderRef">주문번호: {{ order.orderNo }}</div>
                </div>
              </div>

              <div :class="$style.statusContainer">
                <div :class="$style.statusGroup">
                  <div :class="$style.statusBadge">
                    <span :class="getStatusClass(order.orderStatus)">{{
                      order.orderStatus || order.raw?.ordstatval
                    }}</span>
                  </div>
                  <div v-if="order.jobStatus" :class="$style.statusBadge">
                    <span :class="getJobStatusClass(order.jobStatus)">{{ order.jobStatus }}</span>
                  </div>
                  <div v-if="order.shippingInfo?.status" :class="$style.statusBadge">
                    <span :class="getShippingStatusClass(order.shippingInfo.status)">{{
                      order.shippingInfo.status
                    }}</span>
                  </div>
                </div>

                <div :class="$style.deliveryGroup">
                  <div v-if="order.shippingInfo?.method" :class="$style.statusBadge">
                    <span :class="$style.methodBadge">{{ order.shippingInfo.method }}</span>
                  </div>
                  <div v-if="order.shippingInfo?.courier" :class="$style.statusBadge">
                    <span :class="$style.courierBadge">{{ order.shippingInfo.courier }}</span>
                  </div>
                  <div v-if="order.shippingInfo?.trackingNumber" :class="$style.statusBadge">
                    <span :class="$style.trackingBadge">운송장: {{ order.shippingInfo.trackingNumber }}</span>
                  </div>
                </div>

                <div v-if="order.amount !== null" :class="$style.amountInfo">
                  <span>{{ formatAmount(order.amount) }}원</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasMoreOrders" :class="$style.viewMore" @click="viewMoreOrders">더 많은 주문내역 보기</div>
      </div>
    </div>

    <!-- 하단 버튼 영역 -->
    <div :class="$style.orderInputArea">
      <button
        :class="[$style.inquiryButton, { [$style.disabled]: !selectedOrder }]"
        @click="inquireAboutOrder"
        :disabled="!selectedOrder"
      >
        {{ selectedOrder ? "선택한 주문건으로 문의하기" : "주문을 선택해 주세요" }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, useCssModule } from "vue"
import type { Order } from "@wowpress/types"
import backIconUrl from "@/assets/icons/back-arrow-icon.svg"

const $style = useCssModule()

const props = defineProps<{
  orders?: Order[]
  limit?: number
}>()

const emit = defineEmits(["select-order", "view-more", "inquire", "close"])

// 실제 표시할 주문 목록 (props 우선, 없으면 샘플 데이터 사용)
const displayOrders = computed(() => {
  return props.orders
})

// 선택된 주문
const selectedOrder = ref<Order | null>(null)

// 선택된 주문의 상태 업데이트
const selectOrder = (order: Order) => {
  selectedOrder.value = order
  emit("select-order", order)
}

// 더 많은 주문 보기 여부
const hasMoreOrders = computed(() => {
  if (!displayOrders.value) return false

  const displayLimit = props.limit || displayOrders.value.length
  return displayOrders.value.length > displayLimit
})

// 보여질 주문 목록 (limit이 설정된 경우 제한)
const limitedOrders = computed(() => {
  if (props.limit) {
    return displayOrders.value?.slice(0, props.limit)
  }
  return displayOrders.value
})

// 더 많은 주문 보기 클릭 이벤트
const viewMoreOrders = () => {
  emit("view-more")
}

// 선택한 주문으로 문의하기
const inquireAboutOrder = () => {
  if (selectedOrder.value) {
    emit("inquire", selectedOrder.value)
  }
}

// 날짜 포맷팅 함수
const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return ""
  const date = new Date(dateString)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
}

// 금액 포맷팅 함수
const formatAmount = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined) return "0"
  return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

// 상태별 클래스 적용
const getStatusClass = (status: string) => {
  const statusMap: Record<string, string> = {
    배송중: "statusShipping",
    배송완료: "statusDelivered",
    제작중: "statusManufacturing",
    준비중: "statusPreparing",
    환불: "statusRefund",
    입금대기: "statusDefault",
    접수대기: "statusDefault",
    대체입금: "statusDefault",
    접수진행: "statusPreparing",
    상담대기: "statusDefault",
    생산진행: "statusManufacturing",
    생산완료: "statusDelivered",
    출고대기: "statusPreparing",
    출고완료: "statusDelivered",
    주문취소: "statusRefund",
  }

  return $style[statusMap[status] || "statusDefault"]
}

// 작업 상태별 클래스 적용
const getJobStatusClass = (status: string) => {
  const statusMap: Record<string, string> = {
    제작중: "statusManufacturing",
    완료: "statusDelivered",
    대기: "statusDefault",
    진행중: "statusPreparing",
    취소: "statusRefund",
  }

  return $style[statusMap[status] || "statusDefault"]
}

// 배송 상태별 클래스 적용
const getShippingStatusClass = (status: string) => {
  const statusMap: Record<string, string> = {
    배송중: "statusShipping",
    배송완료: "statusDelivered",
    준비중: "statusPreparing",
    출고대기: "statusPreparing",
    출고완료: "statusDelivered",
  }

  return $style[statusMap[status] || "statusDefault"]
}

// 상품 설명 가져오기
const getProductDescription = (order: Order) => {
  if (!order.raw?.ordsub || order.raw?.ordsub.length === 0) return ""

  const descriptions: string[] = []

  for (const sub of order.raw?.ordsub || []) {
    if (sub.ordjob && sub.ordjob.length > 0) {
      for (const job of sub.ordjob) {
        const parts: string[] = []

        if (job.paperrname) parts.push(job.paperrname)
        if (job.color1name) parts.push(job.color1name)
        if (job.sizename) parts.push(job.sizename)
        if (job.jobqty) parts.push(`${job.jobqty}${job.qunit || "장"}`)
        if (sub.ordcnt && sub.ordcnt > 1) parts.push(`x ${sub.ordcnt}건`)
        if (job.optname) parts.push(job.optname)

        descriptions.push(parts.join("/"))
      }
    }
  }

  return descriptions.join(", ") || ""
}
</script>

<style module>
.orderListWindow {
  /* Figma 스타일 */
  width: 343px;
  /* Figma 넓이 */
  height: 583px;
  /* Figma 높이 */
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow:
    5px 14px 80px 0px rgba(26, 26, 26, 0.06),
    10px 10px 40px 0px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: "Noto Sans KR", sans-serif;
}

/* 상단 앱바 - ChatWindow.vue와 동일 */
.chatHeader {
  background-color: #ffecf6;
  /* 연한 분홍 배경 */
  padding: 16px 16px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-shrink: 0;
  gap: 16px;
}

.backButton {
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease-out;
  background-color: transparent;
  border: none;
}

.backButton:hover {
  transform: scale(1.05);
}

.backIcon {
  width: 16px;
  height: 16px;
}

.titleGroup {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.titleGroup h5 {
  font-weight: 700;
  font-size: 13px;
  color: #111111;
  margin: 0;
}

.subtitle {
  font-family: "Noto Sans KR", sans-serif;
  font-weight: 400;
  font-size: 11px;
  color: #888888;
}

/* 주문 목록 컨텐츠 */
.orderContent {
  flex-grow: 1;
  overflow-y: auto;
  padding: 16px;
}

.ordersContainer {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.orderItemWrapper {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  border-bottom: 1px solid #eeeeee;
}

.orderItemWrapper:last-child {
  border-bottom: none;
}

.orderItem {
  border-radius: 12px;
  display: flex;
  padding: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border: 1px solid transparent;
  box-sizing: border-box;
}

.orderItem:hover {
  background-color: #fbfbfb;
}

.orderItem.selected {
  background-color: #fbfbfb;
  border: 1px solid #cf1578;
}

.productImage {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  margin-right: 16px;
}

.productImage img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.orderDetails {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.orderHeader {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  padding: 0 2px;
}

.orderInfo {
  display: flex;
  flex-direction: column;
}

.orderTitle {
  font-weight: 500;
  font-size: 14px;
  color: #111111;
  letter-spacing: -0.7px;
  margin-bottom: 2px;
}

.orderDate,
.orderRef {
  font-size: 10px;
  color: #696969;
  margin-top: 4px;
}

.statusContainer {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.statusGroup,
.deliveryGroup {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.statusBadge {
  display: flex;
  align-items: center;
}

.statusBadge span {
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: -0.3px;
  white-space: nowrap;
}

.methodBadge {
  background-color: #f6f8fa;
  color: #485460;
}

.courierBadge {
  background-color: #e8f0fe;
  color: #1967d2;
}

.trackingBadge {
  background-color: #f0f2f5;
  color: #4a5568;
}

.amountInfo {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #111111;
}

.statusShipping {
  background-color: #e1fcef;
  color: #14804a;
}

.statusDelivered {
  background-color: #e1eafc;
  color: #1a56db;
}

.statusManufacturing {
  background-color: #fcf2e6;
  color: #aa5b00;
}

.statusPreparing {
  background-color: #eef3f5;
  color: #2e4052;
}

.statusRefund {
  background-color: #fcf2e6;
  color: #aa5b00;
}

.statusDefault {
  background-color: #f1f1f1;
  color: #666666;
}

.viewMore {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  color: #696969;
  font-size: 14px;
  font-weight: 400;
  border-top: 0.5px solid #eeeeee;
  cursor: pointer;
}

.viewMore:hover {
  background-color: #fbfbfb;
}

/* 하단 버튼 영역 */
.orderInputArea {
  border-top: 1px solid #eeeeee;
  padding: 20px 16px;
  display: flex;
  justify-content: center;
}

.inquiryButton {
  background-color: #d01578;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow:
    0px 1px 4px rgba(12, 12, 13, 0.1),
    0px 1px 4px rgba(12, 12, 13, 0.05);
  transition: background-color 0.2s;
  height: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.inquiryButton:hover:not(.disabled) {
  background-color: #b7146a;
}

.inquiryButton.disabled {
  background-color: #f8f8f8;
  color: #b0b0b0;
  cursor: not-allowed;
  box-shadow: none;
}
</style>
